import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, useStripe } from "@stripe/react-stripe-js";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import CustomButton from "components/common/Button/CustomButton";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import StripePayment from "pages/Stripe/Payment/StripePayment";
import { apiGetRequest, apiPostRequest } from "helpers/Requests";
import {
  URL_SP_AI_TOP_UP,
  URL_SP_AI_TOP_UP_STATUS,
} from "store/services/URL";
import {
  getStripeListAllCards,
  getStripeListAllCardsLoader,
} from "store/selector/sp.selector";
import { getPaymentSettings } from "store/selector/auth.selector";
import { stripeListAllCard, stripeListAllCardRequest } from "store/actions/sp.actions";
import api from "store/services/sp.services";
import { capitalizeFirstLetter, classNames } from "Utils/utils";
import { toast } from "react-toastify";
import { HiOutlineCreditCard, HiOutlineInformationCircle, HiOutlinePlus, HiArrowLeft } from "react-icons/hi";
import { formatAud } from "./aiUsageChartUtils";
import { getStripeTopUpUserMessage } from "./stripeTopUpUserMessage";
import { parseBalanceAud } from "./topUpBalanceUtils";
import {
  calculateCardProcessingFee,
  calculateTotalWithCardProcessingFee,
  CARD_PROCESSING_FEE_LABEL,
} from "pages/Stripe/stripeCardProcessingFee";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE);
const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 15;
const TOP_UP_EVENT = "vertex360:ai-top-up-notify";

const QUICK_AMOUNTS = [20, 50, 100];

const isValidAmount = (value) => {
  const trimmed = String(value ?? "").trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return false;
  const amount = Number(trimmed);
  return Number.isFinite(amount) && amount > 0;
};

const createIdempotencyKey = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `ai_top_up_${Date.now()}_${Math.random().toString(36).slice(2)}`;
};

const filterValidCards = (cards) =>
  (Array.isArray(cards) ? cards : []).filter(
    (card) => typeof card?.id === "string" && card.id.startsWith("pm_")
  );

const pickDefaultCardId = (cards, defaultPaymentMethodId) => {
  if (!cards.length) return "";
  if (
    defaultPaymentMethodId &&
    cards.some((card) => card.id === defaultPaymentMethodId)
  ) {
    return defaultPaymentMethodId;
  }
  return cards[0].id;
};

const parseTopUpApiPayload = (res) => res?.data?.data ?? res?.data;

const fetchTopUpStatusPayload = async (paymentIntentId) => {
  const res = await apiGetRequest(
    `${URL_SP_AI_TOP_UP_STATUS}?payment_intent_id=${encodeURIComponent(paymentIntentId)}`
  );
  return parseTopUpApiPayload(res);
};

const TopUpModalSkeleton = () => (
  <div className="animate-pulse space-y-5" aria-hidden="true">
    <div>
      <div className="h-4 w-36 rounded bg-gray-200" />
      <div className="mt-2 h-10 w-full rounded-lg bg-gray-200" />
      <div className="mt-2 h-3 w-40 rounded bg-gray-100" />
      <div className="mt-3 flex gap-2">
        <div className="h-9 w-[4.75rem] rounded-md border-2 border-gray-200 bg-gray-50" />
        <div className="h-9 w-[4.75rem] rounded-md border-2 border-gray-200 bg-gray-50" />
        <div className="h-9 w-[4.75rem] rounded-md border-2 border-gray-200 bg-gray-50" />
      </div>
    </div>

    <div>
      <div className="mb-2 flex items-center justify-between">
        <div className="h-4 w-28 rounded bg-gray-200" />
        <div className="h-4 w-16 rounded bg-gray-100" />
      </div>
      <div className="h-28 w-full rounded-lg bg-gray-100" />
    </div>

    <div className="h-12 w-full rounded-lg bg-[#ECF0FD]/60" />

    <div className="flex justify-end">
      <div className="h-10 w-24 rounded-lg bg-gray-200" />
    </div>
  </div>
);

const CardSelectRow = ({ card, selected, onSelect, isDefault }) => {
  const brand = card?.card?.brand || "card";
  const label = card?.metadata?.card_name
    ? capitalizeFirstLetter(card.metadata.card_name)
    : capitalizeFirstLetter(brand);
  const last4 = card?.card?.last4 ?? "----";

  return (
    <button
      type="button"
      onClick={() => onSelect(card.id)}
      className={classNames(
        "group w-full rounded-xl border px-3.5 py-3 text-left transition-all",
        selected
          ? "border-[#4B6CB7] bg-[#ECF0FD] shadow-sm ring-1 ring-[#4B6CB7]/30"
          : "border-gray-200 bg-white hover:border-[#4B6CB7]/40 hover:bg-gray-50/80"
      )}
    >
      <div className="flex items-center gap-3">
        <span
          className={classNames(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border",
            selected
              ? "border-[#4B6CB7]/30 bg-white text-[#4B6CB7]"
              : "border-gray-200 bg-gray-50 text-gray-500 group-hover:border-[#4B6CB7]/20"
          )}
        >
          <HiOutlineCreditCard className="h-5 w-5" />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-semibold text-gray-900">{label}</p>
            {isDefault && (
              <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                Default
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-gray-500">
            {capitalizeFirstLetter(brand)} ·•••• {last4} · Exp{" "}
            {String(card?.card?.exp_month ?? "").padStart(2, "0")}/
            {card?.card?.exp_year ?? ""}
          </p>
        </div>

        <span
          className={classNames(
            "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border",
            selected ? "border-[#4B6CB7]" : "border-gray-300"
          )}
          aria-hidden="true"
        >
          {selected && <span className="h-2 w-2 rounded-full bg-[#4B6CB7]" />}
        </span>
      </div>
    </button>
  );
};

const TopUpModalBody = ({
  amount,
  setAmount,
  selectedCardId,
  setSelectedCardId,
  cards,
  cardsLoading,
  paymentSettings,
  showAddCard,
  setShowAddCard,
  phase,
  errorMessage,
  minAmount,
  maxAmount,
}) => {
  const amountNumber = Number(amount);
  const amountError = useMemo(() => {
    if (!amount) return "";
    if (!isValidAmount(amount)) {
      return "Enter a valid amount with up to 2 decimal places.";
    }
    if (amountNumber < minAmount || amountNumber > maxAmount) {
      return `Amount must be between ${formatAud(minAmount, 2)} and ${formatAud(maxAmount, 2)} AUD.`;
    }
    return "";
  }, [amount, amountNumber, minAmount, maxAmount]);

  const cardProcessingFee = useMemo(() => {
    if (amountError || !isValidAmount(amount)) {
      return 0;
    }
    return calculateCardProcessingFee(amountNumber);
  }, [amount, amountError, amountNumber]);

  const totalCharge = useMemo(() => {
    if (amountError || !isValidAmount(amount)) {
      return 0;
    }
    return calculateTotalWithCardProcessingFee(amountNumber);
  }, [amount, amountError, amountNumber]);

  if (phase === "processing") {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center">
        <CircularLoader width_size="w-10" />
        <p className="mt-4 text-base font-semibold text-gray-900">
          Processing payment…
        </p>
        <p className="mt-2 max-w-sm text-sm text-gray-500">
          Your balance will update once payment is confirmed. This usually takes
          a few seconds.
        </p>
      </div>
    );
  }

  if (showAddCard) {
    return (
      <div>
        <p className="mb-3 text-sm text-gray-600">
          Save a payment card to your account to complete your top-up.
        </p>
        <StripePayment handleClose={() => setShowAddCard(false)} />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Top-up amount (AUD)
        </label>
        <div className="mt-1.5">
          <div
            className={classNames(
              "flex items-center overflow-hidden rounded-lg border bg-white transition-colors",
              amountError
                ? "border-red-300"
                : "border-gray-300 focus-within:border-[#4B6CB7] focus-within:ring-1 focus-within:ring-[#4B6CB7]/20"
            )}
          >
            <span className="border-r border-gray-200 px-3 py-2 text-sm text-gray-500">
              AUD
            </span>
            <input
              type="text"
              inputMode="decimal"
              placeholder="e.g. 50.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="min-w-0 flex-1 border-0 bg-transparent px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
            />
          </div>
          {amountError && (
            <p className="mt-1.5 text-sm text-red-600">{amountError}</p>
          )}
          <p className="mt-1.5 text-xs text-gray-500">
            Min {formatAud(minAmount, 2)} · Max {formatAud(maxAmount, 2)}
          </p>
        </div>
        <div className="mt-3 flex flex-wrap gap-2.5">
          {QUICK_AMOUNTS.map((value) => {
            const isSelected = amount === String(value);
            return (
              <button
                key={value}
                type="button"
                onClick={() => setAmount(String(value))}
                className={classNames(
                  "inline-flex min-w-[4rem] items-center justify-center rounded-md border border-solid px-2.5 py-2 text-sm font-semibold shadow-sm transition-all duration-150",
                  isSelected
                    ? "!border-[#4B6CB7] !bg-[#ECF0FD] text-[#4B6CB7]"
                    : "!border-gray-400 !bg-white text-gray-600 hover:!border-[#4B6CB7] hover:!bg-[#F8FAFF] hover:opacity-80 active:scale-[0.98] active:opacity-70"
                )}
              >
                ${value}
              </button>
            );
          })}
        </div>
      </div>

      {!amountError && isValidAmount(amount) && cardProcessingFee > 0 && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-sm">
          <div className="flex justify-between gap-3 text-gray-700">
            <span>Credit amount</span>
            <span>{formatAud(amountNumber, 2)}</span>
          </div>
          <div className="mt-2 flex justify-between gap-3 text-gray-700">
            <span>{CARD_PROCESSING_FEE_LABEL}</span>
            <span>{formatAud(cardProcessingFee, 2)}</span>
          </div>
          <div className="mt-2 flex justify-between gap-3 border-t border-gray-200 pt-2 font-semibold text-gray-900">
            <span>Total charge</span>
            <span>{formatAud(totalCharge, 2)}</span>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Your wallet will be credited with the credit amount shown above.
          </p>
        </div>
      )}

      <div>
        <div className="mb-2 flex items-center justify-between gap-3">
          <label className="text-sm font-medium text-gray-700">
            Payment method
          </label>
          <button
            type="button"
            onClick={() => setShowAddCard(true)}
            className="inline-flex items-center gap-1 text-sm font-medium text-[#4B6CB7] hover:underline"
          >
            <HiOutlinePlus className="h-4 w-4" />
            Add card
          </button>
        </div>

        {cardsLoading ? (
          <div className="flex justify-center py-8">
            <CircularLoader width_size="w-8" />
          </div>
        ) : cards.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center">
            <HiOutlineCreditCard className="mx-auto h-8 w-8 text-gray-400" />
            <p className="mt-2 text-sm text-gray-600">
              No payment methods saved. Add a card to continue.
            </p>
            <button
              type="button"
              onClick={() => setShowAddCard(true)}
              className="mt-3 text-sm font-semibold text-[#4B6CB7] hover:underline"
            >
              Add card
            </button>
          </div>
        ) : (
          <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-2">
            <div className="max-h-[13.25rem] space-y-2 overflow-y-auto custom-scroll pr-0.5">
              {cards.map((card) => (
                <CardSelectRow
                  key={card.id}
                  card={card}
                  selected={selectedCardId === card.id}
                  onSelect={setSelectedCardId}
                  isDefault={
                    paymentSettings?.default_payment_method_id === card.id
                  }
                />
              ))}
            </div>
            {cards.length > 3 && (
              <p className="mt-2 px-1 text-center text-[11px] text-gray-500">
                Scroll to see {cards.length - 3} more card{cards.length - 3 === 1 ? "" : "s"}
              </p>
            )}
          </div>
        )}
      </div>

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {errorMessage}
        </div>
      )}

      <div className="flex items-start gap-2 rounded-lg bg-[#ECF0FD]/80 px-3 py-2.5 text-sm text-[#4B6CB7]">
        <HiOutlineInformationCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>Your balance will update once payment is confirmed.</span>
      </div>
    </div>
  );
};

const AiTopUpModalInner = ({
  isOpen,
  onClose,
  onSuccess,
  onProcessingStart,
  onProcessingEnd,
  minAmount,
  maxAmount,
}) => {
  const stripe = useStripe();
  const dispatch = useDispatch();
  const cards = useSelector(getStripeListAllCards);
  const cardsLoading = useSelector(getStripeListAllCardsLoader);
  const paymentSettings = useSelector(getPaymentSettings);
  const validCards = useMemo(() => filterValidCards(cards), [cards]);

  const [amount, setAmount] = useState("");
  const [selectedCardId, setSelectedCardId] = useState("");
  const [showAddCard, setShowAddCard] = useState(false);
  const [phase, setPhase] = useState("form");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [initialLoading, setInitialLoading] = useState(false);
  const pendingIntentRef = useRef(null);
  const pollTimerRef = useRef(null);
  const pollAttemptsRef = useRef(0);
  const completedRef = useRef(false);
  const failureHandledRef = useRef(null);
  const successToastRef = useRef(null);

  const clearPoll = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    pollAttemptsRef.current = 0;
  }, []);

  const resetModal = useCallback(() => {
    clearPoll();
    pendingIntentRef.current = null;
    failureHandledRef.current = null;
    setAmount("");
    setSelectedCardId("");
    setShowAddCard(false);
    setPhase("form");
    setSubmitLoading(false);
    setErrorMessage("");
  }, [clearPoll]);

  const enterProcessing = useCallback(
    (paymentIntentId) => {
      pendingIntentRef.current = paymentIntentId;
      setPhase("processing");
      setSubmitLoading(false);
      setErrorMessage("");
      onProcessingStart?.();
    },
    [onProcessingStart]
  );

  const handleClose = useCallback(() => {
    if (phase === "processing") return;
    resetModal();
    onClose();
  }, [onClose, phase, resetModal]);

  const finishSuccess = useCallback(
    async (detail) => {
      if (completedRef.current) return;
      completedRef.current = true;
      clearPoll();
      setSubmitLoading(false);

      const successDetail = {
        payment_intent_id: detail?.payment_intent_id,
        balance_aud: parseBalanceAud(detail?.balance_aud),
        amount_aud: parseBalanceAud(detail?.amount_aud),
      };

      await onSuccess(successDetail);
      resetModal();
      onClose();

      const toastKey = successDetail.payment_intent_id || "ai-top-up";
      if (successToastRef.current !== toastKey) {
        successToastRef.current = toastKey;
        toast.success("AI credits added successfully.", {
          toastId: `ai-top-up-success-${toastKey}`,
        });
      }
    },
    [clearPoll, onClose, onSuccess, resetModal]
  );

  const finishFailure = useCallback(
    (message, paymentIntentId = null) => {
      const intentKey = paymentIntentId || pendingIntentRef.current;
      if (intentKey && failureHandledRef.current === intentKey) {
        return;
      }
      if (intentKey) {
        failureHandledRef.current = intentKey;
      }

      clearPoll();
      pendingIntentRef.current = null;
      setPhase("form");
      setSubmitLoading(false);
      const friendly = getStripeTopUpUserMessage(
        { message },
        message || "Payment failed. Please try again."
      );
      setErrorMessage(friendly);
      onProcessingEnd?.();
      toast.error(friendly);
    },
    [clearPoll, onProcessingEnd]
  );

  const finishFromStatusPayload = useCallback(
    async (paymentIntentId, payload) => {
      if (!payload) return false;

      if (payload.status === "failed" || payload.status === "canceled") {
        finishFailure(
          getStripeTopUpUserMessage(
            { code: "payment_intent_authentication_failure" },
            "Payment failed. Please try another card."
          ),
          paymentIntentId
        );
        return true;
      }

      if (payload.credited) {
        await finishSuccess({
          payment_intent_id: paymentIntentId,
          balance_aud: parseBalanceAud(payload.balance_aud),
          amount_aud: parseBalanceAud(payload.amount_aud),
        });
        return true;
      }

      return false;
    },
    [finishFailure, finishSuccess]
  );

  const resolveTopUpAfterPayment = useCallback(
    async (paymentIntentId, initialPayload = null) => {
      if (await finishFromStatusPayload(paymentIntentId, initialPayload)) {
        return true;
      }

      const QUICK_ATTEMPTS = 5;
      const QUICK_DELAY_MS = 600;

      for (let attempt = 0; attempt < QUICK_ATTEMPTS; attempt += 1) {
        try {
          const statusPayload = await fetchTopUpStatusPayload(paymentIntentId);
          if (await finishFromStatusPayload(paymentIntentId, statusPayload)) {
            return true;
          }
        } catch (err) {
          // Ignore and retry; the background poll is the final fallback.
        }

        if (attempt < QUICK_ATTEMPTS - 1) {
          await new Promise((resolve) => setTimeout(resolve, QUICK_DELAY_MS));
        }
      }

      return false;
    },
    [finishFromStatusPayload]
  );

  const pollTopUpStatus = useCallback(
    (paymentIntentId) => {
      clearPoll();
      pollAttemptsRef.current = 0;

      const tick = async () => {
        pollAttemptsRef.current += 1;
        try {
          const payload = await fetchTopUpStatusPayload(paymentIntentId);
          if (await finishFromStatusPayload(paymentIntentId, payload)) {
            return;
          }
        } catch (err) {
          // Keep polling until timeout; parent refresh is fallback below.
        }

        if (pollAttemptsRef.current >= POLL_MAX_ATTEMPTS) {
          if (completedRef.current) return;

          let successDetail = { payment_intent_id: paymentIntentId };
          try {
            const payload = await fetchTopUpStatusPayload(paymentIntentId);
            const balanceAud = parseBalanceAud(payload?.balance_aud);
            const amountAud = parseBalanceAud(payload?.amount_aud);
            if (payload?.credited && balanceAud != null) {
              successDetail = {
                payment_intent_id: paymentIntentId,
                balance_aud: balanceAud,
                amount_aud: amountAud,
              };
            }
          } catch (err) {
            // Best-effort final status read before closing.
          }

          await finishSuccess(successDetail);
        }
      };

      pollTimerRef.current = setInterval(tick, POLL_INTERVAL_MS);
      tick();
    },
    [clearPoll, finishFromStatusPayload, finishSuccess]
  );

  const startWaitingForCredit = useCallback(
    (paymentIntentId) => {
      pendingIntentRef.current = paymentIntentId;
      pollTopUpStatus(paymentIntentId);
    },
    [pollTopUpStatus]
  );

  useEffect(() => {
    if (!isOpen) {
      setInitialLoading(false);
      return undefined;
    }

    let cancelled = false;
    setInitialLoading(true);

    const loadCards = async () => {
      dispatch(stripeListAllCard.pending);
      dispatch(stripeListAllCard.success([]));
      setSelectedCardId("");
      try {
        const response = await api.stirpeListAllCardApi();
        const cardsData = filterValidCards(response?.payment_methods?.data || []);
        dispatch(stripeListAllCard.success(cardsData));
        setSelectedCardId(
          pickDefaultCardId(cardsData, paymentSettings?.default_payment_method_id)
        );
      } catch (error) {
        dispatch(
          stripeListAllCard.error(error?.message ?? error ?? "Could not load cards.")
        );
      } finally {
        if (!cancelled) {
          setInitialLoading(false);
        }
      }
    };

    loadCards();

    return () => {
      cancelled = true;
    };
  }, [dispatch, isOpen, paymentSettings?.default_payment_method_id]);

  useEffect(() => {
    if (!isOpen || initialLoading || !validCards.length) return;

    setSelectedCardId((current) => {
      if (current && validCards.some((card) => card.id === current)) {
        return current;
      }
      return pickDefaultCardId(
        validCards,
        paymentSettings?.default_payment_method_id
      );
    });
  }, [initialLoading, isOpen, paymentSettings?.default_payment_method_id, validCards]);

  useEffect(() => {
    if (!isOpen) return undefined;

    // Fresh open starts with a clean outcome guard, independent of card reloads.
    completedRef.current = false;
    failureHandledRef.current = null;
    pendingIntentRef.current = null;
    successToastRef.current = null;

    const onNotify = (event) => {
      const message = event?.detail || {};
      const pending = pendingIntentRef.current;

      // Only react to the payment we are actively waiting on (3DS / processing).
      // Prevents stale or duplicate webhook events from firing a second outcome.
      if (!pending) return;
      if (message.payment_intent_id && message.payment_intent_id !== pending) {
        return;
      }

      if (message.status === "success") {
        void finishSuccess({
          ...message,
          balance_aud: parseBalanceAud(message.balance_aud),
          amount_aud: parseBalanceAud(message.amount_aud),
        });
      } else if (message.status === "failed") {
        finishFailure(message.message, message.payment_intent_id);
      }
    };

    window.addEventListener(TOP_UP_EVENT, onNotify);
    return () => {
      window.removeEventListener(TOP_UP_EVENT, onNotify);
      clearPoll();
    };
  }, [clearPoll, finishFailure, finishSuccess, isOpen]);

  useEffect(() => {
    if (!showAddCard || initialLoading) return;
    dispatch(stripeListAllCardRequest());
  }, [dispatch, initialLoading, showAddCard]);

  const handleSubmit = async () => {
    if (!stripe || !selectedCardId || !isValidAmount(amount)) return;

    if (!selectedCardId.startsWith("pm_")) {
      setErrorMessage("Selected payment method is not valid. Please choose a card again.");
      return;
    }

    const amountNumber = Number(amount);
    if (amountNumber < minAmount || amountNumber > maxAmount) {
      setErrorMessage(
        `Amount must be between ${formatAud(minAmount, 2)} and ${formatAud(maxAmount, 2)} AUD.`
      );
      return;
    }

    setSubmitLoading(true);
    setErrorMessage("");

    // New attempt: clear any prior outcome guard so this payment can resolve once.
    completedRef.current = false;
    failureHandledRef.current = null;
    pendingIntentRef.current = null;
    clearPoll();

    try {
      const res = await apiPostRequest(URL_SP_AI_TOP_UP, {
        amount_aud: amountNumber,
        payment_method_id: selectedCardId,
        idempotency_key: createIdempotencyKey(),
      });
      const payload = parseTopUpApiPayload(res);
      const paymentIntentId = payload?.payment_intent_id;
      const status = payload?.status;
      const clientSecret = payload?.client_secret;

      if (status === "requires_action" && clientSecret) {
        pendingIntentRef.current = paymentIntentId;
        const { error, paymentIntent } = await stripe.confirmCardPayment(
          clientSecret
        );
        if (error) {
          finishFailure(
            getStripeTopUpUserMessage(error, "Card authentication failed."),
            paymentIntentId || paymentIntent?.id
          );
          return;
        }
        const resolvedIntentId = paymentIntent?.id || paymentIntentId;
        if (
          paymentIntent?.status === "succeeded" ||
          paymentIntent?.status === "processing"
        ) {
          enterProcessing(resolvedIntentId);
          const resolved = await resolveTopUpAfterPayment(resolvedIntentId);
          if (resolved) {
            return;
          }
          startWaitingForCredit(resolvedIntentId);
          return;
        }
        if (paymentIntent?.status === "requires_payment_method") {
          finishFailure(
            getStripeTopUpUserMessage(
              { code: "payment_intent_authentication_failure" },
              "Card authentication failed. Please try again or use a different card."
            ),
            paymentIntent.id || paymentIntentId
          );
          return;
        }
        finishFailure("Payment could not be completed. Please try again.");
        return;
      }

      if (
        status === "succeeded" ||
        status === "processing" ||
        status === "requires_capture"
      ) {
        enterProcessing(paymentIntentId);
        const resolved = await resolveTopUpAfterPayment(paymentIntentId, payload);
        if (resolved) {
          return;
        }
        startWaitingForCredit(paymentIntentId);
        return;
      }

      finishFailure("Unexpected payment status. Please try again.");
    } catch (err) {
      setSubmitLoading(false);
      setErrorMessage(
        getStripeTopUpUserMessage(err, "Could not start payment. Please try again.")
      );
    }
  };

  const handleBackFromAddCard = useCallback(() => {
    setShowAddCard(false);
  }, []);

  const modalTitle =
    showAddCard && !initialLoading && phase !== "processing" ? (
      <div className="flex items-center gap-2 pr-8">
        <button
          type="button"
          onClick={handleBackFromAddCard}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-white transition-colors hover:bg-white/15"
          aria-label="Back to top up"
        >
          <HiArrowLeft className="h-5 w-5" />
        </button>
        <span className="pt-[5px]">Add payment card</span>
      </div>
    ) : (
      "Top up AI credits"
    );

  const showStickyFooter =
    !initialLoading && !showAddCard && phase !== "processing";

  const amountNumber = Number(amount);
  const amountError = useMemo(() => {
    if (!amount) return "";
    if (!isValidAmount(amount)) {
      return "Enter a valid amount with up to 2 decimal places.";
    }
    if (amountNumber < minAmount || amountNumber > maxAmount) {
      return `Amount must be between ${formatAud(minAmount, 2)} and ${formatAud(maxAmount, 2)} AUD.`;
    }
    return "";
  }, [amount, amountNumber, minAmount, maxAmount]);

  const topUpFooter = showStickyFooter ? (
    <CustomButton
      label="Top up"
      type="button"
      clickHandler={handleSubmit}
      isDisabled={
        submitLoading ||
        !selectedCardId ||
        !isValidAmount(amount) ||
        !!amountError
      }
      showLoading={submitLoading}
    />
  ) : null;

  return (
    <ModalSkeleton
      isOpen={isOpen}
      title={modalTitle}
      closeModal={phase === "processing" || initialLoading ? null : handleClose}
      cssClass="sm:max-w-2xl w-full"
      stretchBody={showStickyFooter}
      modalFooter={topUpFooter}
    >
      {initialLoading ? (
        <TopUpModalSkeleton />
      ) : (
        <TopUpModalBody
          amount={amount}
          setAmount={setAmount}
          selectedCardId={selectedCardId}
          setSelectedCardId={setSelectedCardId}
          cards={validCards}
          cardsLoading={cardsLoading}
          paymentSettings={paymentSettings}
          showAddCard={showAddCard}
          setShowAddCard={setShowAddCard}
          phase={phase}
          errorMessage={errorMessage}
          minAmount={minAmount}
          maxAmount={maxAmount}
        />
      )}
    </ModalSkeleton>
  );
};

const AiTopUpModal = (props) => (
  <Elements stripe={stripePromise}>
    <AiTopUpModalInner {...props} />
  </Elements>
);

export default AiTopUpModal;
export { TOP_UP_EVENT };
